import React from "react";
import { useLanguage } from "../contexts/LanguageContext";

export const AboutSection = () => {
  const { t, language } = useLanguage();
  const aboutContent = t("aboutContent");

  // Function to bold key strategic terms
  const highlightKeywords = (text, lang) => {
    let formattedText = text;

    if (lang === "fr") {
      const keywords = [
        "opérateurs",
        "équipes terrain",
        "cause racine",
        "correction définitive",
        "sans préjugé",
        "fiabilité",
        "performance",
        "surveillance",
        "optimisation",
        "analyse à distance",
        "expert",
        "urgence réelle",
        "solution définitive",
      ];
      keywords.forEach((keyword) => {
        const regex = new RegExp(
          `\\b(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})\\b`,
          "gi",
        );
        formattedText = formattedText.replace(
          regex,
          '<strong class="font-semibold text-[#e8e8e8]">$1</strong>',
        );
      });
    } else {
      const keywords = [
        "operators",
        "field teams",
        "root cause",
        "permanent correction",
        "without bias",
        "reliability",
        "performance",
        "monitoring",
        "optimization",
        "remote analysis",
        "expert",
        "real emergency",
        "definitive solution",
      ];
      keywords.forEach((keyword) => {
        const regex = new RegExp(
          `\\b(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})\\b`,
          "gi",
        );
        formattedText = formattedText.replace(
          regex,
          '<strong class="font-semibold text-[#e8e8e8]">$1</strong>',
        );
      });
    }

    return formattedText;
  };

  // Format lists with colored bullets
  const formatLists = (text) => {
    return text.replace(
      /•\s/g,
      '<span class="inline-block mr-3 text-[#5a6e54] font-bold">•</span>',
    );
  };

  // Check if line is a section title
  const isSectionTitle = (text) => {
    const titles = [
      "Our Purpose",
      "Our Method: Listen, Question, Correct",
      "Remote Intervention",
      "Area of Expertise",
      "Industries Served",
      "Why Choose EyaTech?",
      "Our Mission",
      "Notre raison d'être",
      "Notre méthode : Écouter, Interroger, Corriger",
      "Intervention à distance",
      "Rayon d'expertise",
      "Secteurs desservis",
      "Pourquoi choisir EyaTech ?",
      "Notre mission",
    ];
    return titles.some((title) => text.trim().startsWith(title));
  };

  // Special handling for "Our Method" section to format as visual list
  const formatMethodSection = (text, lang) => {
    if (lang === "fr" && text.includes("Notre méthode")) {
      const parts = text.split("\n");
      return parts
        .map((line, idx) => {
          if (idx === 0) return line; // Title line
          if (line.includes("Nous écoutons")) {
            return `<div class="flex items-start mt-4 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          if (line.includes("Nous interrogeons")) {
            return `<div class="flex items-start mt-3 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          if (line.includes("En croisant")) {
            return `<div class="flex items-start mt-3 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          return line;
        })
        .join("\n");
    } else if (lang === "en" && text.includes("Our Method")) {
      const parts = text.split("\n");
      return parts
        .map((line, idx) => {
          if (idx === 0) return line; // Title line
          if (line.includes("We listen")) {
            return `<div class="flex items-start mt-4 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          if (line.includes("We question")) {
            return `<div class="flex items-start mt-3 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          if (line.includes("By cross-referencing")) {
            return `<div class="flex items-start mt-3 ml-6"><span class="text-[#5a6e54] font-bold mr-3">↳</span><span class="flex-1">${line}</span></div>`;
          }
          return line;
        })
        .join("\n");
    }
    return text;
  };

  return (
    <section
      id="about"
      className="relative min-h-screen flex flex-col justify-start overflow-hidden pt-28 md:pt-32 pb-16 md:pb-20 px-4 md:px-8"
    >
      <div className="max-w-4xl mx-auto w-full">
        {/* Main Title */}
        <div className="mb-10 md:mb-16 pb-6 border-b border-[#2a2a2a]">
          <h2
            className="
              uppercase text-[#d8d8d8] opacity-50 font-bold
              text-5xl md:text-7xl
              tracking-[0.12em] md:tracking-[0.25em]
              whitespace-normal break-words
            "
            style={{ fontFamily: "var(--font-eurostile)" }}
          >
            {aboutContent.title}
          </h2>
        </div>

        <div className="space-y-14 md:space-y-16">
          {aboutContent.paragraphs.map((paragraph, index) => {
            const isMethodSection =
              paragraph.includes("méthode") || paragraph.includes("Method");

            const formattedParagraph = isMethodSection
              ? formatMethodSection(paragraph, language)
              : paragraph;

            const processedLines = formattedParagraph.split("\n");

            return (
              <React.Fragment key={index}>
                <div className="relative pl-5 md:pl-8 py-8 bg-gradient-to-r from-[#121416]/40 to-transparent border-l border-[#2a2a2a]">
                  {processedLines.map((line, lineIndex) => {
                    const isTitle = isSectionTitle(line);
                    const isBullet = line.trim().startsWith("•");
                    const isEmpty = !line.trim();

                    if (isEmpty) return <div key={lineIndex} className="h-6" />;

                    if (isTitle) {
                      return (
                        <div key={lineIndex} className="mb-8 mt-2">
                          <h3
                            className="
                              uppercase text-[#d8d8d8] opacity-50 font-bold
                              text-2xl md:text-[28px]
                              tracking-[0.10em] md:tracking-wide
                              whitespace-normal break-words
                            "
                            style={{ fontFamily: "var(--font-eurostile)" }}
                            dangerouslySetInnerHTML={{ __html: line }}
                          />
                          <div className="h-[1px] w-24 bg-[#5a6e54] mt-3 opacity-40" />
                        </div>
                      );
                    }

                    if (isBullet) {
                      return (
                        <div
                          key={lineIndex}
                          className="text-[#d8d8d8] ml-4 md:ml-6 my-3 flex items-start"
                          style={{
                            fontFamily: "var(--font-eurostile)",
                            fontSize: "21px",
                            lineHeight: "1.6",
                            fontWeight: "400",
                          }}
                        >
                          <span className="text-[#5a6e54] font-bold mr-4 flex-shrink-0">
                            •
                          </span>
                          <span
                            className="flex-1"
                            dangerouslySetInnerHTML={{
                              __html: highlightKeywords(
                                line.replace("•", "").trim(),
                                language,
                              ),
                            }}
                          />
                        </div>
                      );
                    }

                    if (line.includes('<div class="flex')) {
                      return (
                        <div
                          key={lineIndex}
                          style={{
                            fontFamily: "var(--font-eurostile)",
                            fontSize: "21px",
                            lineHeight: "1.6",
                            fontWeight: "400",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: highlightKeywords(line, language),
                          }}
                        />
                      );
                    }

                    return (
                      <p
                        key={lineIndex}
                        className="text-[#d8d8d8] mb-6 max-w-3xl"
                        style={{
                          fontFamily: "var(--font-eurostile)",
                          fontSize: "21px",
                          lineHeight: "1.6",
                          fontWeight: "400",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formatLists(highlightKeywords(line, language)),
                        }}
                      />
                    );
                  })}
                </div>

                {/* Specialization line and Allen-Bradley logo after first paragraph */}
                {index === 0 && (
                  <div className="relative pl-5 md:pl-8 py-6 bg-gradient-to-r from-[#121416]/40 to-transparent border-l border-[#2a2a2a]">
                    <div className="flex flex-wrap items-center gap-4 md:gap-6">
                      <p
                        className="text-[#888] italic flex-1 min-w-[220px]"
                        style={{
                          fontFamily: "var(--font-eurostile)",
                          fontSize: "18px",
                          lineHeight: "1.6",
                          fontWeight: "400",
                        }}
                      >
                        {aboutContent.specialization}
                      </p>
                      <img
                        src="https://customer-assets.emergentagent.com/job_eya-command/artifacts/w1q1b8cu_image.png"
                        alt="Allen-Bradley"
                        className="h-7 md:h-8 max-w-full"
                        style={{
                          filter: "invert(1) brightness(0.8)",
                          mixBlendMode: "screen",
                        }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};
